import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Svg,
  Path,
} from "@react-pdf/renderer";
import logo from "../../assets/Main_Logo_AI_Colored_Padding.png";

// Colors pulled from screenshot vibes
const COLORS = {
  green: "#14833b",
  greenDark: "#2e7d32",
  gray100: "#f5f6f7",
  gray200: "#eef0f1",
  gray300: "#e5e7eb",
  textMain: "#111827",
  textSub: "#6b7280",
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 24,
    paddingHorizontal: 24,
    paddingBottom: 70, // reserve space for footer
    fontFamily: "Helvetica",
  },

  // Header
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 6,
  },
  titleIcon: { width: 20, height: 20, marginRight: 8 },
  titleText: { fontSize: 20, fontWeight: 700, color: COLORS.green },
  subtitle: { fontSize: 11, color: COLORS.textSub, marginBottom: 16 },

  // Grade card
  card: {
    backgroundColor: COLORS.gray100,
    borderColor: COLORS.gray200,
    borderWidth: 1,
    borderRadius: 6,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginBottom: 18,
  },
  cardRow: { flexDirection: "row", alignItems: "center" },
  cardLabel: { fontSize: 12, fontWeight: 700, color: COLORS.textMain, marginRight: 8 },
  gradePill: {
    backgroundColor: COLORS.greenDark,
    color: "#fff",
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 3,
    fontSize: 11,
    fontWeight: 700,
  },

  // Section
  sectionTitle: { fontSize: 13, fontWeight: 700, color: COLORS.textMain, marginBottom: 8, marginTop: 8 },

  // Table
  table: {
    borderColor: COLORS.gray300,
    borderWidth: 1,
    borderRadius: 6,
    overflow: "hidden",
    marginBottom: 16,
  },
  thead: {
    backgroundColor: COLORS.gray100,
    flexDirection: "row",
    borderBottomColor: COLORS.gray300,
    borderBottomWidth: 1,
  },
  th: { fontSize: 11, fontWeight: 700, paddingVertical: 8, paddingHorizontal: 10 },
  row: {
    flexDirection: "row",
    borderBottomColor: COLORS.gray300,
    borderBottomWidth: 1,
  },
  td: { fontSize: 11, paddingVertical: 10, paddingHorizontal: 10, color: COLORS.textMain },

  // Column widths
  colDomain: { flexBasis: "40%", flexGrow: 0, flexShrink: 0 },
  colIP: { flexBasis: "40%", flexGrow: 0, flexShrink: 0 },
  colBlacklisted: { flexBasis: "20%", flexGrow: 0, flexShrink: 0 },

  // “No/Yes” badge
  statusPill: {
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 3,
    fontSize: 10,
    fontWeight: 700,
    textAlign: "center",
    alignSelf: "flex-start",
  },
  statusNo: { backgroundColor: COLORS.greenDark, color: "#fff" },
  statusYes: { backgroundColor: "#c62828", color: "#fff" },

  // Footer
  footer: {
    position: "absolute",
    bottom: 16,
    left: 24,
    right: 24,
    height: 24,
    borderTopWidth: 1,
    borderTopColor: COLORS.gray300,
    paddingTop: 6,
    fontSize: 9,
    color: "#6b7280",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  footerLeft: { fontSize: 9 },
  footerBrand: { flexDirection: "row", alignItems: "center" },
  footerLogo: { width: 60 },
});

const Footer = () => {
  const dateText = new Date().toLocaleString("en-US");
  return (
    <View style={styles.footer} fixed>
      <Text style={styles.footerLeft}>
        {`Generated by Portend AI • Confidential • ${dateText}`}
      </Text>
      <View style={styles.footerBrand}>
        {logo ? <Image style={styles.footerLogo} src={logo} /> : null}
      </View>
    </View>
  );
};

const HeaderIcon = () => (
  <Svg style={styles.titleIcon} viewBox="0 0 24 24">
    {/* Simple “scan” style icon */}
    <Path fill={COLORS.green} d="M3 6V4h4v2H5v2H3V6Zm18 0V4h-4v2h2v2h2V6ZM3 18v2h4v-2H5v-2H3v2Zm16 0v2h2v-2h-2v-2h-2v2h2ZM7 9h10v2H7V9Zm0 4h10v2H7v-2Z"/>
  </Svg>
);

const Pill = ({ yes }) => (
  <Text style={[styles.statusPill, yes ? styles.statusYes : styles.statusNo]}>
    {yes ? "Yes" : "No"}
  </Text>
);

const BlacklistResultsPDF = ({ data, pageTitle }) => {
  const domainRows = data?.result?.domainResults || [];
  const mailRows = data?.result?.mailServerResults || [];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Title */}
        <View style={styles.titleRow}>
          <HeaderIcon />
          <Text style={styles.titleText}>{pageTitle || "Blacklist Scan Report"}</Text>
        </View>

        {/* Subtitle */}
        <Text style={styles.subtitle}>
          {`Scan Results for ${data?.domain || "-"} • Checked on ${new Date(data?.date).toLocaleString()}`}
        </Text>

        {/* Grade Card */}
        <View style={styles.card}>
          <View style={styles.cardRow}>
            <Text style={styles.cardLabel}>Blacklist Grade:</Text>
            <Text style={styles.gradePill}>{data?.grades?.blacklistGrade || "-"}</Text>
          </View>
        </View>

        {/* Domain Blacklist Check */}
        <Text style={styles.sectionTitle}>Domain Blacklist Check</Text>
        <View style={styles.table}>
          <View style={styles.thead}>
            <Text style={[styles.th, styles.colDomain]}>Domain</Text>
            <Text style={[styles.th, styles.colIP]}>IP Address</Text>
            <Text style={[styles.th, styles.colBlacklisted]}>Blacklisted</Text>
          </View>
          {domainRows.map((r, i) => (
            <View key={i} style={styles.row}>
              <Text style={[styles.td, styles.colDomain]}>{r.input}</Text>
              <Text style={[styles.td, styles.colIP]}>{r.ip}</Text>
              <View style={[styles.td, styles.colBlacklisted]}>
                <Pill yes={!!r.blacklisted} />
              </View>
            </View>
          ))}
        </View>

        {/* Mail Server Blacklist Check */}
        <Text style={styles.sectionTitle}>Mail Server Blacklist Check</Text>
        <View style={styles.table}>
          <View style={styles.thead}>
            <Text style={[styles.th, styles.colDomain]}>Mail Server</Text>
            <Text style={[styles.th, styles.colIP]}>IP Address</Text>
            <Text style={[styles.th, styles.colBlacklisted]}>Blacklisted</Text>
          </View>
          {mailRows.map((r, i) => (
            <View key={i} style={styles.row}>
              <Text style={[styles.td, styles.colDomain]}>{r.input}</Text>
              <Text style={[styles.td, styles.colIP]}>{r.ip}</Text>
              <View style={[styles.td, styles.colBlacklisted]}>
                <Pill yes={!!r.blacklisted} />
              </View>
            </View>
          ))}
        </View>

        {/* Footer */}
        <Footer />
      </Page>
    </Document>
  );
};

export default BlacklistResultsPDF;
